import React, { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";

import { useConfig } from "../hooks/useConfig";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { AeronetViewProps } from "../types";
import { getOriginalUrl } from "../utils/originalUrl";
import InvoiceDetail from "./InvoiceDetail";
import InvoiceList from "./InvoiceList";

/**
 * Invoices module route shell.
 *
 * Permission resolution mirrors `quotes/Quotes.tsx`:
 *   invoices_create -> canCreate
 *   invoices_Edit   -> canEdit
 *   invoices_Delete -> canDelete
 *
 * After useConfig strips the "invoices_" prefix, keys are
 * `create` / `Edit` / `Delete`.
 */
const Invoices: React.FC<React.PropsWithChildren<AeronetViewProps>> = (
  props
) => {
  const { originalUrl: domain, ...otherProps } = props;
  const originalUrl = getOriginalUrl(domain, "function=invoices");
  const invoiceConfig = useConfig("invoices");
  const currentUser = useCurrentUser();

  const [hasInvoiceCreatePermission, setHasInvoiceCreatePermission] =
    useState(false);
  const [hasInvoiceEditPermission, setHasInvoiceEditPermission] =
    useState(false);
  const [hasInvoiceDeletePermission, setHasInvoiceDeletePermission] =
    useState(false);

  useEffect(() => {
    if (!invoiceConfig || !currentUser) return;
    const perms = currentUser.permissions?.invoices ?? [];

    setHasInvoiceCreatePermission(
      !!invoiceConfig.create && perms.includes(invoiceConfig.create)
    );
    setHasInvoiceEditPermission(
      !!invoiceConfig.Edit && perms.includes(invoiceConfig.Edit)
    );
    setHasInvoiceDeletePermission(
      !!invoiceConfig.Delete && perms.includes(invoiceConfig.Delete)
    );
  }, [currentUser, invoiceConfig]);

  return (
    <Routes>
      {hasInvoiceEditPermission ? (
        <Route
          path=":id"
          element={<InvoiceDetail originalUrl={originalUrl} />}
        />
      ) : null}
      {hasInvoiceCreatePermission ? (
        <Route
          path="/create"
          element={<InvoiceDetail originalUrl={originalUrl} />}
        />
      ) : null}
      <Route
        path="*"
        element={
          <InvoiceList
            originalUrl={originalUrl}
            invoiceConfig={invoiceConfig}
            canCreateInvoice={hasInvoiceCreatePermission}
            canDeleteInvoice={hasInvoiceDeletePermission}
            {...otherProps}
          />
        }
      />
    </Routes>
  );
};

export default Invoices;
